// @givmo/app-charity — the charity's Profile PAGE (PE-WEB1): the org-generic `OrgProfilePage` bound
// to this class's operations, words, Details panel and public header.
//
// ══ WHAT THIS HOST SUPPLIES, AND NOTHING ELSE ═══════════════════════════════════════════════════
//
//   * the scope — the ACTIVE org's charity id (`orgRef`);
//   * the four operations on this class's plane — the generated `charityAdminProfilePage*` SDK ops
//     (`GET/PUT /charity-admin/charities/{id}/page`, `POST …/page/images`) and the one public read
//     (`GET /public/profiles/charity/{id}/page`), all un-wrapped for the package;
//   * the Details tab — the live CPA-WEB6 editor's body (`ProfileFactsPanel`): mission / website /
//     contact + logo + the read-only verification domain, same ops, same Owner gate;
//   * the View-as-public HEADER — the donate page's own hero (`PublicProfileHero`, extracted from
//     `apps/donate`) over the same facts the donate page shows: name, logo, the mission excerpt cut
//     by the same rule, and "Show more" opening to the full mission and the detail rows (CPA-BE44;
//     the rows from `GET /charities/{id}`, the donate page's own read). The tab renders the page
//     exactly as a visitor sees it (D-PE-17 (6)); the
//     one part it cannot show, the donation controls, is named by a one-line stand-in;
//   * `canEdit` — `profile:write` off the console's ONE standing read (the org-level string PE-BE5
//     minted: a Global Admin, or any admin with an Owner-tier grant in any module, D-PE-14). A
//     not-reported standing is PERMISSIVE (the console's own posture for write controls — a slow
//     read must not strand a real admin; the server refuses a write it will not accept);
//   * the words: the org's name for the header chip, the one-line mission hint above a story (the
//     charity's ETL / portal `mission` already renders on its page, plan §1.5), and where the real
//     public page lives — the donate page, addressed by its `charityId` search param;
//   * the Branding and Collateral tabs (EMC-WEB11) while `VITE_ORG_BRANDING_ENABLED` is on, with the
//     organization QR design editor housed on Branding (D-EMC-20 (c)) while its own knob is on too —
//     the editor on the public code, gated on `manage_org_qr` exactly as the `/profile/qr` page is;
//   * the address: the active tab rides `?tab=details|content|branding|collateral|public` (D-PE-17 (7)) — the route
//     wrapper below reads it and writes it back, `replace` so a tab change is not a history entry.
//
// The page never asks a flag endpoint what is on: availability is the owner read's own 404
// (plan §1.4; the feature-switch rule). An unknown block kind renders nothing and is reported
// once as a non-fatal (§1.2).

import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { sdk, type ProfilePageReplaceIn } from '@givmo/api-client';
import {
  charitiesGetCharityOptions,
  charityAdminGetCharityProfileOptions,
} from '@givmo/api-client/query';
import {
  PUBLIC_PROFILE_DESCRIPTION_EXCERPT_MAX,
  PublicProfileHero,
  publicProfileDetails,
  publicProfileExcerpt,
  publicProfileInitials,
} from '@givmo/design-system';
import { captureNonFatal } from '@givmo/observability';
import {
  CAPABILITY_MANAGE_ORG_QR,
  OrgProfilePage,
  OrgProfileQrPage,
  OrgPublicQrCard,
  OrgQrDesignEditor,
  holdsCapability,
  PROFILE_COPY,
  PROFILE_PAGE_TEST_ID_PREFIX,
  profileTabFromSearch,
  profileTabToSearch,
  type ProfilePageOperations,
  type ProfilePageVocab,
  type ProfileTab,
} from '@givmo/org-console';
import { useCharityConsole } from '../../console/charity-console-context';
import { getDonateWebBaseUrl, isOrgBrandingEnabled, isOrgQrDesignEnabled } from '../../lib/env';
import { useUnsavedChanges } from '../../lib/unsaved-changes';
import { useActiveOrg } from '../../org/active-org-context';
import { charityQrSaveScope } from '../fundraisers-module/fundraisers-mount';
import {
  CHARITY_PUBLIC_QR_LINE,
  CHARITY_PUBLIC_QR_PREVIEW_NOUN,
  CHARITY_QR_TARGET_UNAVAILABLE,
} from '../org-qr/charity-qr-vocab';
import { useCharityLogoSrc } from '../org-qr/use-charity-logo';
import { ProfileFactsPanel } from './profile-facts-panel';
import { PROFILE_ROUTES } from './profile-routes';

/** The org-level string that admits page-content writes (PE-BE5). */
export const PROFILE_WRITE_PERMISSION = 'profile:write';

/** The one-line hint above a story: the mission already renders on the public page (plan §1.5). */
export const STORY_MISSION_HINT = 'Your mission already appears on your page.';

/** The stand-in for the one part of the donate page the console cannot show between header and blocks. */
export const PUBLIC_PAGE_INTERSTITIAL = 'Donation options appear here.';

/** Where this charity's public page is on the web — the donate page, keyed by its search param. */
export function charityPublicPageHref(charityId: string): string {
  return `${getDonateWebBaseUrl()}/?charityId=${encodeURIComponent(charityId)}`;
}

/** The non-fatal a block of a kind this build does not know raises (§1.2) — once per block. */
export function reportUnknownBlockKind(kind: string): void {
  captureNonFatal('profile page: unknown block kind', { kind, host: 'charity' });
}

export interface CharityProfilePageProps {
  /** The active tab when the route mirrors it to the address. */
  tab?: ProfileTab;
  onTabChange?: (tab: ProfileTab) => void;
}

export function charityProfileOperations(charityId: string): ProfilePageOperations {
  return {
    getOwnerPage: async () => {
      const { data } = await sdk.charityAdminProfilePageGetCharityPage({
        path: { charity_id: charityId },
        throwOnError: true,
      });
      return data;
    },
    replacePage: async (body: ProfilePageReplaceIn) => {
      const { data } = await sdk.charityAdminProfilePageReplaceCharityPage({
        path: { charity_id: charityId },
        body,
        throwOnError: true,
      });
      return data;
    },
    uploadImage: async (file: File) => {
      const { data } = await sdk.charityAdminProfilePageUploadCharityPageImage({
        path: { charity_id: charityId },
        body: { file },
        throwOnError: true,
      });
      return data;
    },
    getPublicPage: async () => {
      const { data } = await sdk.profilePagesPublicGetPublicProfilePage({
        path: { entity_type: 'charity', entity_id: charityId },
        throwOnError: true,
      });
      return data;
    },
  };
}

/**
 * The public header, exactly as the donate page draws it: the same component over the same facts
 * (name, logo, the mission cut at the same word boundary, and "Show more" with the charity's detail
 * rows). Reads the profile the Details tab already loaded — one query key, one cache entry — and
 * the four detail facts from the donate page's own read, `GET /charities/{id}` (`legal_name`, `EIN`,
 * `category`, `organization_domain`), so the tab opens to the rows a visitor sees.
 */
export function CharityPublicHeader({ charityId }: { charityId: string }) {
  const profileQuery = useQuery({
    ...charityAdminGetCharityProfileOptions({ path: { charity_id: charityId } }),
    enabled: charityId.length > 0,
  });
  const charityQuery = useQuery({
    ...charitiesGetCharityOptions({ path: { charity_id: charityId } }),
    enabled: charityId.length > 0,
  });
  const profile = profileQuery.data?.profile;
  // Drawn once both reads have answered, so the control never appears after the header does.
  if (profile === undefined || charityQuery.isPending) return null;
  const charity = charityQuery.data;
  return (
    <PublicProfileHero
      name={profile.name}
      imageUrl={profile.logo_url ?? null}
      fallbackText={publicProfileInitials(profile.name)}
      description={publicProfileExcerpt(
        profile.mission ?? null,
        PUBLIC_PROFILE_DESCRIPTION_EXCERPT_MAX,
      )}
      more={{
        mission: profile.mission ?? null,
        details: charity
          ? publicProfileDetails({
              legalName: charity.legal_name,
              ein: charity.EIN,
              category: charity.category,
              organizationDomain: charity.organization_domain,
            })
          : null,
      }}
      headingLevel={2}
      testId="profile-page-public-hero"
    />
  );
}

/**
 * The organization QR design, housed on the Branding tab (D-EMC-20 (c)): the ONE shared editor on the
 * public code, the same words as the `/profile/qr` page. The page mounts it only for a person holding
 * `manage_org_qr` (its gate there too); without it, or while the standing read is still out, the
 * section is absent, never an editor whose every render the server would refuse.
 */
function CharityBrandingQrDesign({
  orgRef,
  onUnsavedChange,
}: {
  orgRef: string;
  onUnsavedChange: (unsaved: boolean) => void;
}) {
  const { activeMembership } = useActiveOrg();
  const orgLogoSrc = useCharityLogoSrc();
  const orgName = activeMembership?.name ?? 'Your organization';
  // Branding's own save line heads it (`saveLine`), so the tab saves one way; the lede says what the
  // design governs, which the "Save as default" card's scope line says on the QR pages.
  return (
    <OrgQrDesignEditor
      orgRef={orgRef}
      target={{ kind: 'public' }}
      canSave
      vocab={{
        orgName,
        saveScope: charityQrSaveScope(orgName),
        targetUnavailable: CHARITY_QR_TARGET_UNAVAILABLE,
      }}
      orgLogoSrc={orgLogoSrc}
      saveLine={{ lede: PROFILE_COPY.branding.qr.lede(orgName) }}
      onUnsavedChange={onUnsavedChange}
      testIdPrefix={`${PROFILE_PAGE_TEST_ID_PREFIX}-branding`}
    />
  );
}

export function CharityProfilePage({ tab, onTabChange }: CharityProfilePageProps) {
  const { activeMembership, activeCharityId } = useActiveOrg();
  const { read } = useCharityConsole();
  const navigate = useNavigate();
  const orgRef = activeCharityId ?? '';

  const operations = useMemo(() => charityProfileOperations(orgRef), [orgRef]);
  const canEdit = read.permissions === null || read.permissions.includes(PROFILE_WRITE_PERMISSION);
  const vocab: ProfilePageVocab = useMemo(
    () => ({
      orgName: activeMembership?.name,
      storyHint: STORY_MISSION_HINT,
      publicPageHref: orgRef.length > 0 ? charityPublicPageHref(orgRef) : null,
      publicPageStatement:
        'Your public page is the donate page people reach from your QR code and links.',
      publicPageInterstitial: PUBLIC_PAGE_INTERSTITIAL,
    }),
    [activeMembership?.name, orgRef],
  );
  // Branding's edits not yet saved: the console's guard asks before a navigation, an organization
  // switch or closing the tab discards them, as it does for Details.
  // The housed QR design's unsaved working design counts too.
  const [brandingUnsaved, setBrandingUnsaved] = useState(false);
  const [qrUnsaved, setQrUnsaved] = useState(false);
  useUnsavedChanges(brandingUnsaved || qrUnsaved);

  return (
    <OrgProfilePage
      orgRef={orgRef}
      operations={operations}
      vocab={vocab}
      factsPanel={<ProfileFactsPanel />}
      publicHeader={<CharityPublicHeader charityId={orgRef} />}
      publicQrSlot={
        isOrgQrDesignEnabled() ? (
          <OrgPublicQrCard
            orgRef={orgRef}
            capabilities={read.capabilities}
            line={CHARITY_PUBLIC_QR_LINE}
            targetUnavailable={CHARITY_QR_TARGET_UNAVAILABLE}
            onCustomize={() => void navigate({ to: PROFILE_ROUTES.qr })}
            testIdPrefix={`${PROFILE_PAGE_TEST_ID_PREFIX}-preview`}
          />
        ) : undefined
      }
      brand={
        isOrgBrandingEnabled()
          ? {
              // The title and tabs stop under the console's own sticky bar.
              stickyTop: 'var(--charity-app-bar-h)',
              onUnsavedChange: setBrandingUnsaved,
              qrDesignUnsaved: qrUnsaved,
              qrDesignSlot:
                isOrgQrDesignEnabled() &&
                holdsCapability(read.capabilities, CAPABILITY_MANAGE_ORG_QR) ? (
                  <CharityBrandingQrDesign orgRef={orgRef} onUnsavedChange={setQrUnsaved} />
                ) : undefined,
            }
          : undefined
      }
      onUnknownKind={reportUnknownBlockKind}
      canEdit={canEdit}
      tab={tab}
      onTabChange={onTabChange}
    />
  );
}

/**
 * The route's component once the page is revealed: the tab off the address (`?tab=`), and every
 * tab change written back to it — `replace`, so switching tabs never stacks history entries.
 */
export function CharityProfileRoute() {
  const search = useSearch({ strict: false }) as { tab?: unknown };
  const navigate = useNavigate();
  const tab = profileTabFromSearch(search.tab) ?? 'facts';
  const onTabChange = useCallback(
    (next: ProfileTab) => {
      void navigate({
        to: PROFILE_ROUTES.editor,
        search: { tab: profileTabToSearch(next) },
        replace: true,
        resetScroll: false,
      });
    },
    [navigate],
  );
  return <CharityProfilePage tab={tab} onTabChange={onTabChange} />;
}

/**
 * The Public tab's QR design page (PE-WEB11, `/profile/qr`): the shared editor on the charity's public
 * code, the charity's words, and the charity's own logo off the profile read. Registered only while
 * both the Profile page and the organization QR design are revealed.
 */
export function CharityProfileQrRoute() {
  const { activeMembership, activeCharityId } = useActiveOrg();
  const { read, pending, errored, refetch } = useCharityConsole();
  const navigate = useNavigate();
  const orgLogoSrc = useCharityLogoSrc();
  const orgName = activeMembership?.name ?? 'Your organization';
  return (
    <OrgProfileQrPage
      orgRef={activeCharityId ?? ''}
      capabilities={read.capabilities}
      standing={{ pending, errored }}
      onRetry={refetch}
      vocab={{
        orgName,
        saveScope: charityQrSaveScope(orgName),
        targetUnavailable: CHARITY_QR_TARGET_UNAVAILABLE,
      }}
      previewNoun={CHARITY_PUBLIC_QR_PREVIEW_NOUN}
      orgLogoSrc={orgLogoSrc}
      onBack={() =>
        void navigate({ to: PROFILE_ROUTES.editor, search: { tab: profileTabToSearch('preview') } })
      }
      testIdPrefix={PROFILE_PAGE_TEST_ID_PREFIX}
    />
  );
}
